import { TextInput } from 'components/FormUtils/TextInput'
import { useState } from 'react'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { Plus } from 'lucide-react'
import { NumericInput } from '../../FormUtils/NumericInput'
import Select from '../../FormUtils/Select'
import { Control, useWatch } from 'react-hook-form'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { CompanyTypeSlug, SubCategory } from 'models/companies'
import { ProductPrediction } from 'models/deals'
import { DealsFormValues } from 'pages/app/deals/MobileDealsCreate'

// Type for company details - can be either ChainDetail or IContactCompany
interface CompanyDetails {
  name?: string
  chain?: string
  sub_category?: SubCategory
}

interface MobileDealDetailsStepProps {
  prediction: ProductPrediction
  control: Control<DealsFormValues>
  companyTypeSlug?: CompanyTypeSlug
  companyDetails?: CompanyDetails
  selectedVariantName?: string | null
  onSelectProductVariant: () => void
  onClearProductVariant: () => void
}

export default function MobileDealDetailsStep({
  prediction,
  control,
  companyTypeSlug,
  companyDetails,
  selectedVariantName,
  onSelectProductVariant,
  onClearProductVariant,
}: MobileDealDetailsStepProps) {
  const api = useApiService()
  const featureFlag = useFeatureFlagService()
  const { data: stageOptions } = api.useGetSalesStageOptions()
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })

  // Filter campaigns by the contact company's sub_category slug if available
  const campaignTypeFilter =
    companyDetails?.sub_category?.slug ?? companyTypeSlug
  const { data: campaignOptions } = api.useGetCampaignsOptions({
    typeOrTypeId: campaignTypeFilter,
    requireFilter: true, // Prevent selecting campaigns when subcategory is unknown
  })

  const [overridesExpanded, setOverridesExpanded] = useState(false)

  const watch = useWatch({ control })

  const stage = watch?.deals?.[0]?.sales_stage
  const avgDaysToClose = stageOptions?.find((it) => it.value === stage)
    ?.original?.avg_days_to_close

  const date = new Date()
  if (avgDaysToClose) {
    date.setDate(date.getDate() + avgDaysToClose)
  }

  return (
    <div className="px-4 space-y-4">
      {/* Deal Name */}
      <div>
        <TextInput
          control={control}
          name="deals.0.name"
          label="Deal Name"
          placeholder={
            companyDetails?.chain ||
            companyDetails?.name ||
            'Enter custom deal name'
          }
          optional={true}
          maxLength={100}
        />
      </div>

      {/* Product (Read-only display) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Product
        </label>
        <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700">
          {prediction.product.name}
        </div>
      </div>

      {/* Product Variant - Button that opens modal */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Product Variant <span className="text-gray-400">- optional</span>
        </label>
        {selectedVariantName ? (
          <div className="flex items-center justify-between gap-4 px-3 py-2 rounded-md border-[1px] border-brand-hover bg-surface-hover">
            <div className="flex-1">
              <div className="font-semibold text-brand">
                {selectedVariantName}
              </div>
            </div>
            <button
              type="button"
              onClick={onClearProductVariant}
              className="shrink-0 p-1 text-brand-hover hover:text-brand-pressed transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onSelectProductVariant}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md border-[1px] border-brand-hover bg-surface-hover hover:bg-surface-hover-deep transition-colors"
          >
            <Plus size={16} className="text-brand" />
            <span className="font-medium text-sm text-brand">
              Select Product Variant
            </span>
          </button>
        )}
      </div>

      {/* Sales Stage */}
      <div>
        <Select
          options={stageOptions}
          control={control}
          name="deals.0.sales_stage"
          label="Sales Stage"
        />
        {stage && (
          <span className="text-sm text-gray-600 mt-2 block">
            <b>Estimated Close Date:</b> {date.toLocaleDateString('en-US')}
          </span>
        )}
      </div>

      {/* Account Owner */}
      <div>
        <Select
          options={userOptions}
          control={control}
          name="deals.0.account_owner"
          label="Account Owner"
        />
      </div>

      {/* Origin Campaign */}
      {featureFlag.enableCampaigns && (
        <div>
          <Select
            options={campaignOptions}
            control={control}
            name="deals.0.origin_campaign_id"
            label="Origin Campaign"
          />
        </div>
      )}

      {/* Deal Overrides Section */}
      <div className="mt-6">
        <div className="bg-surface-muted rounded-lg p-4">
          <button
            type="button"
            onClick={() => setOverridesExpanded(!overridesExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">
                Deal Overrides
              </h4>
              <span className="bg-sage-lt text-gray-500 text-xs px-2 py-1 rounded-full">
                Optional
              </span>
            </div>
            {overridesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {overridesExpanded && (
            <div className="mt-4 space-y-4">
              <NumericInput
                control={control}
                fieldName="deals.0.monthly_volume_override"
                label="Est. Monthly Volume"
                optional={true}
              />
              <NumericInput
                control={control}
                fieldName="deals.0.monthly_revenue_override"
                label="Est. Monthly Revenue"
                optional={true}
                decimalScale={2}
              />
              <TextInput
                label="Est. Close Date"
                control={control}
                name="deals.0.close_date_override"
                type="date"
                optional={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
